import type React from "react"
import { useState } from "react"
import { toast } from "sonner"
import { submitAnonymousFeedback } from "../../features/jobs/jobAPI"
import { StarRating } from "./star-rating"

interface AnonymousFeedbackFormProps {
    token: string
}

export const AnonymousFeedbackForm: React.FC<AnonymousFeedbackFormProps> = ({ token }) => {
    const [fullName, setFullName] = useState("")
    const [comment, setComment] = useState("")
    const [rating, setRating] = useState(0)
    const [showValidation, setShowValidation] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)

    const handleSave = async () => {
        const trimmedName = fullName.trim()
        const trimmedComment = comment.trim()

        if (!trimmedName || !trimmedComment || rating === 0) {
            setShowValidation(true)
            setTimeout(() => setShowValidation(false), 3000)
            return
        }

        try {
            setIsSaving(true)
            const result = await submitAnonymousFeedback({
                token,
                full_name: trimmedName,
                comment: trimmedComment,
                rating,
            })

            if (result.error) {
                toast.error(result.error)
                return
            }

            toast.success("Feedback submitted successfully.")
            setIsSubmitted(true)
            setFullName("")
            setComment("")
            setRating(0)
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to submit feedback."
            toast.error(message)
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="relative space-y-6">
            <div
                className={`rounded-md bg-white p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 ${showValidation ? "blur-md" : ""}`}
            >
                <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Add Feedback</h2>
                    <StarRating rating={rating} editable onChange={setRating} />
                </div>

                <div className="mb-5">
                    <label htmlFor="anonymous-feedback-name" className="mb-2 block text-sm font-bold text-gray-700">
                        Full name
                    </label>
                    <input
                        id="anonymous-feedback-name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter your full name"
                        className="w-full rounded-md border border-gray-200 bg-[#F9F9F9] px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-[#ABD405] focus:bg-white"
                    />
                </div>

                <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Type here...."
                    className="min-h-[120px] w-full resize-none rounded-md border-t-2 border-black/10 pt-4 text-gray-600 outline-hidden placeholder:text-gray-300"
                />
            </div>

            <div className="relative flex flex-col items-end gap-3">
                {showValidation && (
                    <>
                        <div
                            className="fixed inset-0 z-40 bg-black/5 backdrop-blur-sm animate-in fade-in duration-300"
                            onClick={() => setShowValidation(false)}
                        />
                        <div className="absolute bottom-full right-0 z-50 mb-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <div className="relative w-[360px] overflow-hidden rounded-md bg-white p-8 text-center shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-50">
                                <h3 className="text-lg font-bold leading-snug text-gray-900">
                                    Please add your name, rating, and feedback to submit.
                                </h3>
                                <div className="absolute -bottom-2 right-12 h-4 w-4 rotate-45 bg-white border-b border-r border-gray-100/50 shadow-[4px_4px_10px_rgba(0,0,0,0.02)]" />
                            </div>
                        </div>
                    </>
                )}

                {isSubmitted && (
                    <p className="text-sm font-medium text-[#ABD405]">
                        Thank you. Your feedback has been submitted.
                    </p>
                )}

                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-cta rounded-md px-16 py-2 font-bold disabled:opacity-60"
                >
                    {isSaving ? "Saving..." : "Save"}
                </button>
            </div>
        </div>
    )
}
